import React, { MouseEventHandler, ReactNode } from 'react';
import { flex } from '@fabric-ui/theme';
import { LoadingButton } from '@mui/lab';
import { Box, Button, ButtonProps, IconButton, IconButtonProps, SxProps, Theme, useTheme } from '@mui/material';
import { AvTooltip, Loading } from './';

export enum ButtonKinds {
  TOGGLE = 'TOGGLE',
  ICON = 'ICON',
}

export enum ButtonPosition {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  CENTER = 'CENTER',
}

interface Props {
  children: ReactNode;
  loading?: boolean;
  kind?: ButtonKinds;
  value?: any;
  isActive?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  tooltip?: string;
  position?: ButtonPosition;
  sx?: SxProps<Theme>;
  ariaLabel?: string;
  toolTipProps?: typeof AvTooltip;
}

export const AvButton = ({
  children,
  loading,
  kind,
  isActive,
  tooltip,
  position,
  toolTipProps,
  ...props
}: Partial<(ButtonProps & Props) | any>) => {
  const theme = useTheme();

  const commonButtonStyle = {
    [ButtonKinds.TOGGLE]: {
      ...(isActive && {
        background: theme.palette.colors.primary[150],
        color: theme.palette.colors.neutrals[800],
      }),
    },
  };

  // TODO: Figure out if Button is exactly like LoadingButton when `loading=false`

  if (loading !== undefined) {
    return (
      <LoadingButton {...props} loading={loading} loadingIndicator={<Loading height={20} />}>
        {children}
      </LoadingButton>
    );
  }

  if (kind === ButtonKinds.TOGGLE) {
    return (
      <Button
        variant="filter"
        {...props}
        sx={{
          height: '24px',
          padding: '3px 8px',
          backgroundColor: theme.palette.colors.neutrals[300],
          borderRadius:
            position === ButtonPosition.LEFT ? '10px 0px 0px 10px' : position === ButtonPosition.RIGHT ? '0px 10px 10px 0px' : '0px',
          '&, &:hover': commonButtonStyle[ButtonKinds.TOGGLE],
          ...props.sx,
        }}>
        <AvTooltip title={tooltip}>
          <Box sx={{ ...flex.col, fontSize: 12, lineHeight: 1 }}>{children}</Box>
        </AvTooltip>
      </Button>
    );
  }

  if (kind === ButtonKinds.ICON) {
    const iconButton = <IconButton {...(props as IconButtonProps)}>{children}</IconButton>;
    return tooltip ? (
      <AvTooltip title={tooltip} {...toolTipProps}>
        {iconButton}
      </AvTooltip>
    ) : (
      iconButton
    );
  }

  const button = <Button {...props}>{children}</Button>;
  return tooltip ? (
    <AvTooltip title={tooltip} {...toolTipProps}>
      <div>{button}</div>
    </AvTooltip>
  ) : (
    button
  );
};
